/**
 * Landmarks and the palm frame
 *
 * Segments the hand from the background, finds the finger tips, the webs between
 * them and the thumb, and builds a normalised palm coordinate frame. Everything
 * downstream works in that frame, so a photo taken at an angle or a different
 * distance still lands the mounts in the right place.
 *
 * ── Which path this is ──────────────────────────────────────────────────────
 *
 * This is the **classical fallback** landmark path: skin-chroma segmentation and
 * a radial profile around the palm centre. The intended primary is a 21-point
 * ML hand model (see `landmark-provider.js`, decision ◇1/◇18) — it copes with
 * skin tone, background and pose far better than a chroma threshold can. Both
 * paths produce the same `Landmarks` object, so nothing downstream knows which
 * ran.
 *
 * ⚠ Segmentation without a model is genuinely fragile: skin tone, cluttered
 * backgrounds and warm indoor light all move chroma. Hence a **quality gate**
 * that refuses a photograph it cannot segment, with a specific reason, in place
 * of a confident wrong trace. Nothing here is ever "best effort".
 *
 * ── The palm frame ──────────────────────────────────────────────────────────
 *
 *   u  0 at the thumb-side edge of the palm, 1 at the little-finger (ulnar) edge
 *   v  0 at the wrist crease, 1 at the finger-web line
 *
 * Because u is measured *from the thumb*, a left hand comes out already mirrored
 * to a right one: the same classifier reads both. `handedness()` reports which
 * it was, assuming the palm faces the camera.
 *
 * © Manish Jagdish Thatte
 */

import { label8, makeBinary, makeGray } from './image.js';

export const SKIN = Object.freeze({ crMin: 133, crMax: 173, cbMin: 77, cbMax: 127 });

/** Fraction of the frame outside which a "hand" is refused. */
export const GATE = Object.freeze({ minFraction: 0.04, maxFraction: 0.75, minKeepShare: 0.6, maxBorderShare: 0.5 });

// ── segmentation ────────────────────────────────────────────────────────────

/** Skin mask by YCbCr chroma. `img` is RGBA. Returns a binary image. */
export function skinMask(img, skin = SKIN) {
  const out = makeBinary(img.width, img.height); const d = img.data;
  for (let i = 0, j = 0; i < out.data.length; i++, j += 4) {
    const r = d[j]; const g = d[j + 1]; const b = d[j + 2];
    const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
    const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
    if (cr >= skin.crMin && cr <= skin.crMax && cb >= skin.cbMin && cb <= skin.cbMax) out.data[i] = 1;
  }
  return out;
}

function morph(bin, radius, dilate) {
  const { width: w, height: h, data } = bin; const out = makeBinary(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let v = dilate ? 0 : 1;
    // Outside the frame counts as *unknown*, not background: a hand cropped by the
    // edge of the photo must stay cropped, not be eaten back from it.
    for (let dy = -radius; dy <= radius && (dilate ? !v : v); dy++) {
      const yy = y + dy; if (yy < 0 || yy >= h) continue;
      for (let dx = -radius; dx <= radius; dx++) {
        const xx = x + dx; if (xx < 0 || xx >= w) continue;
        if (dilate ? data[yy * w + xx] : !data[yy * w + xx]) { v = dilate ? 1 : 0; break; }
      }
    }
    out.data[y * w + x] = v;
  }
  return out;
}
export const dilate = (b, r = 1) => morph(b, r, true);
export const erode = (b, r = 1) => morph(b, r, false);

/** Fill interior holes (background not connected to the border). */
export function fillHoles(bin) {
  const { width: w, height: h } = bin;
  const inv = makeBinary(w, h); for (let i = 0; i < inv.data.length; i++) inv.data[i] = bin.data[i] ? 0 : 1;
  const { labels } = label8(inv);
  const border = new Set();
  for (let x = 0; x < w; x++) { border.add(labels[x]); border.add(labels[(h - 1) * w + x]); }
  for (let y = 0; y < h; y++) { border.add(labels[y * w]); border.add(labels[y * w + w - 1]); }
  const out = makeBinary(w, h);
  for (let i = 0; i < out.data.length; i++) out.data[i] = bin.data[i] || (inv.data[i] && !border.has(labels[i])) ? 1 : 0;
  return out;
}

/**
 * Segment the hand. Returns `{ mask, ok, reason, fraction, borderShare }`.
 * `reason` is one of `no-skin`, `too-much-skin`, `fragmented`, `cropped`, or null.
 */
export function segmentHand(img, opts = {}) {
  const raw = opts.mask ?? skinMask(img, opts.skin);
  const w = raw.width; const h = raw.height; const total = w * h;
  let px = 0; for (let i = 0; i < total; i++) px += raw.data[i];
  const fraction = px / total;
  const fail = (reason, extra = {}) => ({ mask: makeBinary(w, h), ok: false, reason, fraction, borderShare: 0, ...extra });
  if (fraction < GATE.minFraction) return fail('no-skin');
  if (fraction > GATE.maxFraction) return fail('too-much-skin');

  const closed = erode(dilate(raw, 1), 1);                         // close small gaps (palm creases are darker than the skin threshold allows)
  const { labels, sizes } = label8(closed);
  let big = 1; for (let i = 1; i < sizes.length; i++) if (sizes[i] > sizes[big]) big = i;
  const keep = sizes[big] / (sizes.reduce((a, b) => a + b, 0) || 1);
  if (keep < GATE.minKeepShare) return fail('fragmented', { keepShare: keep });
  const mask = makeBinary(w, h); for (let i = 0; i < total; i++) mask.data[i] = labels[i] === big ? 1 : 0;
  const filled = fillHoles(mask);

  let border = 0; let onBorder = 0;
  for (let x = 0; x < w; x++) { if (filled.data[x]) onBorder++; if (filled.data[(h - 1) * w + x]) onBorder++; }
  for (let y = 0; y < h; y++) { if (filled.data[y * w]) onBorder++; if (filled.data[y * w + w - 1]) onBorder++; }
  border = onBorder / (2 * (w + h));
  return { mask: filled, ok: true, reason: null, fraction, borderShare: border, keepShare: keep };
}

// ── distance transform (3-4 chamfer) ────────────────────────────────────────

export function distanceTransform(bin) {
  const { width: w, height: h, data } = bin; const INF = 1e9;
  const d = makeGray(w, h);
  for (let i = 0; i < d.data.length; i++) d.data[i] = data[i] ? INF : 0;
  const a = 3; const b = 4;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x; if (!d.data[i]) continue; let v = d.data[i];
    if (x > 0) v = Math.min(v, d.data[i - 1] + a);
    if (y > 0) { v = Math.min(v, d.data[i - w] + a); if (x > 0) v = Math.min(v, d.data[i - w - 1] + b); if (x < w - 1) v = Math.min(v, d.data[i - w + 1] + b); }
    d.data[i] = v;
  }
  for (let y = h - 1; y >= 0; y--) for (let x = w - 1; x >= 0; x--) {
    const i = y * w + x; if (!d.data[i]) continue; let v = d.data[i];
    if (x < w - 1) v = Math.min(v, d.data[i + 1] + a);
    if (y < h - 1) { v = Math.min(v, d.data[i + w] + a); if (x < w - 1) v = Math.min(v, d.data[i + w + 1] + b); if (x > 0) v = Math.min(v, d.data[i + w - 1] + b); }
    d.data[i] = v;
  }
  // border pixels are "outside" for the purposes of palm width: shrink by treating the frame edge as background
  for (let i = 0; i < d.data.length; i++) d.data[i] /= a;
  return d;
}

// ── landmarks ───────────────────────────────────────────────────────────────

/** Ray-march from (cx,cy) along angle `a` until the mask ends. Returns `{ r, open }` (`open` = hit the image border still inside). */
function ray(mask, cx, cy, a, maxR) {
  const { width: w, height: h, data } = mask; const dx = Math.cos(a); const dy = Math.sin(a);
  let r = 0;
  for (; r < maxR; r += 1) {
    const x = Math.round(cx + dx * r); const y = Math.round(cy + dy * r);
    if (x < 0 || y < 0 || x >= w || y >= h) return { r, open: true };
    if (!data[y * w + x]) return { r: r - 1, open: false };
  }
  return { r: maxR, open: true };
}

function smooth(arr, k) {
  const n = arr.length; const out = new Float64Array(n);
  for (let i = 0; i < n; i++) { let s = 0; for (let j = -k; j <= k; j++) s += arr[(i + j + n) % n]; out[i] = s / (2 * k + 1); }
  return out;
}

/**
 * Find fingers, webs and the palm centre in a hand mask.
 *
 * Returns `{ ok, reason, centre, radius, palmWidth, tips, webs, wrist, fingerDir, thumbSideDir, confidence, profile }`
 * with `tips = [thumb, index, middle, ring, little]` and `webs = [thumb–index,
 * index–middle, middle–ring, ring–little]`, all `[x, y]`.
 * On failure `ok` is false and `reason` says why (`fingers-not-found`, `no-palm`).
 */
export function findLandmarks(mask) {
  const { width: w, height: h } = mask;
  const dt = distanceTransform(mask);
  let best = 0; let bi = -1;
  for (let i = 0; i < dt.data.length; i++) if (dt.data[i] > best) { best = dt.data[i]; bi = i; }
  if (bi < 0 || best < 6) return { ok: false, reason: 'no-palm' };
  // Palm centre = centroid of the pixels within 10 % of the maximal inscribed radius (stabler than one pixel).
  let sx = 0; let sy = 0; let sn = 0;
  for (let i = 0; i < dt.data.length; i++) if (dt.data[i] >= best * 0.9) { sx += i % w; sy += Math.floor(i / w); sn++; }
  const cx = sx / sn; const cy = sy / sn; const radius = best;

  const N = 360; const R = new Float64Array(N); const open = new Uint8Array(N);
  const maxR = Math.hypot(w, h);
  for (let k = 0; k < N; k++) {
    const rr = ray(mask, cx, cy, (k * 2 * Math.PI) / N, maxR);
    R[k] = rr.r; open[k] = rr.open ? 1 : 0;
  }
  const S = smooth(R, 2);

  // local maxima with prominence
  const peaks = [];
  for (let k = 0; k < N; k++) {
    const p = S[k];
    if (p < S[(k - 1 + N) % N] || p <= S[(k + 1) % N] || p < 1.3 * radius) continue;
    let lmin = p; for (let j = 1; j < N / 2; j++) { const v = S[(k - j + N) % N]; if (v < lmin) lmin = v; if (v > p) break; }
    let rmin = p; for (let j = 1; j < N / 2; j++) { const v = S[(k + j) % N]; if (v < rmin) rmin = v; if (v > p) break; }
    const prom = p - Math.max(lmin, rmin);
    if (prom < 0.15 * radius) continue;
    const half = p - prom / 2;
    let lw = 0; while (lw < N / 2 && S[(k - lw - 1 + N) % N] >= half) lw++;
    let rw = 0; while (rw < N / 2 && S[(k + rw + 1) % N] >= half) rw++;
    peaks.push({ k, r: R[k], s: p, prom, width: lw + rw + 1, isOpen: open[k] === 1 });
  }
  const fingerLike = peaks.filter((p) => p.width <= 34 && !p.isOpen);
  if (fingerLike.length < 5) return { ok: false, reason: 'fingers-not-found', centre: [cx, cy], radius, found: fingerLike.length };

  // Choose the five strongest peaks that fit inside a window of ≤ 170°.
  fingerLike.sort((a, b) => a.k - b.k);
  let chosen = null;
  const M = fingerLike.length;
  for (let s = 0; s < M; s++) {
    const win = [];
    for (let j = 0; j < M; j++) {
      const p = fingerLike[(s + j) % M]; const dk = (p.k - fingerLike[s].k + N) % N;
      if (dk <= 170) win.push(p);
    }
    if (win.length >= 5) {
      const top = win.slice().sort((a, b) => b.prom - a.prom).slice(0, 5).sort((a, b) => ((a.k - fingerLike[s].k + N) % N) - ((b.k - fingerLike[s].k + N) % N));
      const score = top.reduce((a, p) => a + p.prom, 0);
      if (!chosen || score > chosen.score) chosen = { top, score };
    }
  }
  if (!chosen) return { ok: false, reason: 'fingers-not-found', centre: [cx, cy], radius, found: fingerLike.length };
  let five = chosen.top;

  // Gaps between consecutive fingers (angular), then the widest end-gap marks the thumb.
  const ang = (a, b) => (b.k - a.k + N) % N;
  const gaps = [ang(five[0], five[1]), ang(five[1], five[2]), ang(five[2], five[3]), ang(five[3], five[4])];
  // The thumb sits at whichever end has the larger gap to its neighbour.
  if (gaps[3] > gaps[0]) { five = five.slice().reverse(); gaps.reverse(); }
  // webs = R minima between consecutive tips (scanning in the direction from five[0] to five[4])
  const dirSign = ang(five[0], five[4]) < N / 2 ? 1 : -1;
  const at = (k) => [cx + Math.cos((k * 2 * Math.PI) / N) * R[((k % N) + N) % N], cy + Math.sin((k * 2 * Math.PI) / N) * R[((k % N) + N) % N]];
  // A ray from the palm centre meets a finger's *side*, not its tip, unless the
  // finger points straight at the centre. Refine: the tip is the mask pixel
  // farthest from the centre within the finger's own angular sector.
  const sector = (p, i) => {
    const near = five.filter((q) => q !== p).map((q) => Math.min(ang(p, q), ang(q, p)));
    return Math.max(4, Math.min(20, 0.5 * Math.min(...near)));
  };
  const tips = five.map((p, i) => {
    const half = sector(p, i); let bestD = -1; let bp = at(p.k);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (!mask.data[y * w + x]) continue;
      const dx = x - cx; const dy = y - cy; const d = Math.hypot(dx, dy); if (d <= bestD) continue;
      let a = Math.atan2(dy, dx) * 180 / Math.PI; if (a < 0) a += 360;
      let da = Math.abs(a - p.k); if (da > 180) da = 360 - da;
      if (da <= half) { bestD = d; bp = [x, y]; }
    }
    return bp;
  });
  const webs = [];
  for (let i = 0; i < 4; i++) {
    let minK = five[i].k; let minR = Infinity;
    const steps = dirSign === 1 ? ang(five[i], five[i + 1]) : ang(five[i + 1], five[i]);
    for (let j = 0; j <= steps; j++) {
      const kk = (five[i].k + dirSign * j + N) % N;
      if (S[kk] < minR) { minR = S[kk]; minK = kk; }
    }
    webs.push(at(minK));
  }

  const midTip = tips[2];
  let fx = midTip[0] - cx; let fy = midTip[1] - cy; const fl = Math.hypot(fx, fy) || 1; fx /= fl; fy /= fl;
  // n: thumb → little finger, perpendicular to f
  let nx = tips[4][0] - tips[0][0]; let ny = tips[4][1] - tips[0][1];
  const proj = nx * fx + ny * fy; nx -= proj * fx; ny -= proj * fy;
  const nl = Math.hypot(nx, ny) || 1; nx /= nl; ny /= nl;

  // palm width: run of mask through the centre along n
  const along = (sign) => ray(mask, cx, cy, Math.atan2(ny * sign, nx * sign), maxR).r;
  const palmWidth = along(1) + along(-1);

  // web-line midpoint (index–middle, middle–ring, ring–little) → palm length by symmetry about the centre
  const M3 = [webs[1], webs[2], webs[3]];
  const mwx = (M3[0][0] + M3[1][0] + M3[2][0]) / 3; const mwy = (M3[0][1] + M3[1][1] + M3[2][1]) / 3;
  // Wrist: where the cross-section of the mask, walking away from the fingers,
  // first narrows clearly below the palm's own width — the forearm meeting the
  // palm. If the arm is not in the picture (or never narrows), fall back to
  // assuming the centre sits half-way between the web line and the wrist.
  const run = (x, y, dx, dy) => { let r = 0; for (; r < maxR; r++) { const xx = Math.round(x + dx * r); const yy = Math.round(y + dy * r); if (xx < 0 || yy < 0 || xx >= w || yy >= h || !mask.data[yy * w + xx]) break; } return r; };
  // The thumb ball bulges the thumb side of the lower palm, so only the ulnar half is measured, along the palm's own axis.
  const lateral = (cx - mwx) * nx + (cy - mwy) * ny;
  const section = (sDist) => run(mwx - fx * sDist + nx * lateral, mwy - fy * sDist + ny * lateral, nx, ny);
  let wrist = null; let wristBy = 'symmetry';
  {
    let widest = 0;
    const step = Math.max(1, palmWidth / 60);
    for (let d = 0.15 * palmWidth; d <= 0.8 * palmWidth; d += step) widest = Math.max(widest, section(d));
    for (let d = 0.6 * palmWidth; d <= 1.6 * palmWidth; d += step) {
      const wd = section(d);
      if (wd > 0 && wd < 0.82 * widest) { wrist = [mwx - fx * d, mwy - fy * d]; wristBy = 'narrowing'; break; }
    }
  }
  if (!wrist) {
    const webDist = (mwx - cx) * fx + (mwy - cy) * fy;
    wrist = [cx - fx * Math.abs(webDist), cy - fy * Math.abs(webDist)];
  }

  const lengths = tips.map((t, i) => Math.hypot(t[0] - cx, t[1] - cy) - (i === 0 ? 0 : 0));
  const meanProm = five.reduce((a, p) => a + p.prom, 0) / 5 / radius;
  const confidence = Math.max(0, Math.min(1, 0.4 + 0.4 * Math.min(1, meanProm) + (fingerLike.length === 5 ? 0.2 : 0)));

  return {
    ok: true, reason: null, centre: [cx, cy], radius, palmWidth, tips, webs, wrist,
    fingerDir: [fx, fy], thumbSideDir: [-nx, -ny], ulnarDir: [nx, ny],
    webMid: [mwx, mwy], wristBy, fingerLengths: lengths, confidence, extra: fingerLike.length - 5
  };
}

// ── palm frame ──────────────────────────────────────────────────────────────

/**
 * Build the normalised palm frame from landmarks.
 * `frame.origin` is the thumb-side wrist corner; `u` runs to the ulnar edge, `v` to the web line.
 */
export function palmFrame(lm) {
  if (!lm?.ok) return null;
  const [fx, fy] = lm.fingerDir; const [nx, ny] = lm.ulnarDir;
  const width = lm.palmWidth || lm.radius * 2;
  const length = Math.hypot(lm.webMid[0] - lm.wrist[0], lm.webMid[1] - lm.wrist[1]) || width;
  const origin = [lm.wrist[0] - nx * (width / 2), lm.wrist[1] - ny * (width / 2)];
  return { origin, ex: [nx, ny], ey: [fx, fy], width, length, handedness: handedness(null, lm) };
}

export function toPalmSpace(point, frame) {
  const dx = point[0] - frame.origin[0]; const dy = point[1] - frame.origin[1];
  return { u: (dx * frame.ex[0] + dy * frame.ex[1]) / frame.width, v: (dx * frame.ey[0] + dy * frame.ey[1]) / frame.length };
}

export function fromPalmSpace(u, v, frame) {
  return [frame.origin[0] + frame.ex[0] * u * frame.width + frame.ey[0] * v * frame.length,
    frame.origin[1] + frame.ex[1] * u * frame.width + frame.ey[1] * v * frame.length];
}

/**
 * 'left' | 'right' | 'unknown'. Assumes the **palm** faces the camera: the sign of
 * the turn from the finger direction to the thumb→little direction flips for the
 * back of the hand, and a photo alone does not say which side it shows.
 */
export function handedness(_mask, lm) {
  if (!lm?.ok) return 'unknown';
  const [fx, fy] = lm.fingerDir; const [nx, ny] = lm.ulnarDir;
  const cross = fx * ny - fy * nx;
  if (Math.abs(cross) < 0.5) return 'unknown';
  return cross < 0 ? 'right' : 'left';
}
